/**
 * The live order queue.
 *
 * The screen a counter works from all day. It shows active orders as cards in a
 * responsive grid, filtered by the question a staff member is actually asking:
 * "what needs starting", "what is ready", "what happened today".
 *
 * Status changes go to `merchant_set_order_status`, which enforces the legal
 * transition table, stamps the right timestamp, re-estimates prep time on
 * acceptance, and records an audit event. The local queue is updated from the
 * server's response immediately rather than waiting for the realtime echo, so
 * the card moves the instant it is tapped.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ActivityIndicator, Pressable, View } from 'react-native';
import {
  actBy,
  boardBand,
  boardComparator,
  type BoardOrder,
  type MessageKey,
  bookedInsidePrep,
  formatClock,
  formatDayTime,
  formatDuration,
  CHIP_LABEL_KEYS,
  formatMoney,
  orderChips,
  type OrderChip,
  inBoardTab,
  orderStatusLabel,
  scanTarget,
  totalItemCount,
  type BoardBand,
  type BoardTab,
  type Order,
  type OrderStatus,
} from '@yummy/shared';
import {
  blockNumber,
  fetchOrderByCode,
  fetchOrderByPickupToken,
  setOrderPaid,
  setOrderStatus,
  type CancelCode,
} from '@yummy/db';
import {
  Button,
  Callout,
  Card,
  CardGrid,
  EmptyState,
  ErrorBanner,
  HeadingStat,
  Icon,
  IconControl,
  LoadingState,
  PageHeading,
  Modal,
  QrScanner,
  Row,
  Spacer,
  Screen,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  SegmentedControl,
  Stack,
  StatRow,
  StatusChip,
  Text,
  TextField,
  ToneBadge,
  cn,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useAction,
  useNow,
  useResponsive,
  useT,
  useTheme,
  useToast,
} from '@yummy/ui-rnr';

import { useOrders } from '../app/orders';
import { useDayOrders } from '../app/day-orders';
import { OrderCard } from '../components/OrderCard';
import { supabase } from '../lib/supabase';
import {
  ArrowLeft,
  ArrowUpDown,
  CalendarDays,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  ScanLine,
} from 'lucide-react-native';

/**
 * How often the bands are regrouped, in milliseconds.
 *
 * A band boundary is a minute-scale event, so ten seconds is finer than anything
 * it decides and coarse enough that two hundred cards are not regrouped on every
 * one-second tick of the shared clock. It matches the interval the offline poll
 * already runs at.
 */
const BAND_TICK_MS = 10_000;

/**
 * The tab bar is a status filter and nothing else: the three status tabs are a
 * partition, so their counts add up to `All`'s and no two can disagree. Time
 * pressure is the bands', and kind or exception is the card's.
 */
type Filter = BoardTab;

/** The bands the grid draws, in the order work has to happen. */
const BANDS: BoardBand[] = ['late', 'now', 'next'];

/**
 * The search, which is one field for four things the counter might have.
 *
 * The number before the code: a hawker's staff hear "forty-six" and type 46, and
 * the code is never spoken aloud.
 */
function matchesSearch(order: Order, needle: string): boolean {
  return (
    order.code.toLowerCase().includes(needle) ||
    String(order.order_number).includes(needle) ||
    order.customer_name.toLowerCase().includes(needle) ||
    order.customer_phone.replace(/\s/g, '').includes(needle.replace(/\s/g, ''))
  );
}

/**
 * The tab a status belongs to, for a press on a card's status chip.
 *
 * The tab bar's partition read the other way: a merchant looking at an order that
 * says "Preparing" and pressing it is asking to see the rest of what is cooking.
 */
const TAB_FOR_STATUS: Record<OrderStatus, Filter> = {
  pending: 'accept',
  accepted: 'cooking',
  preparing: 'cooking',
  ready: 'ready',
  completed: 'all',
  cancelled: 'all',
};

/** The band names, in the order work has to happen. */
const BAND_LABEL = {
  late: 'merchant.orders.bandLate',
  now: 'merchant.orders.bandNow',
  next: 'merchant.orders.bandNext',
  later: 'merchant.orders.bandNext',
} as const;

/**
 * What the band means, in the tab it is being read in.
 *
 * A ready order that is late is waiting on the customer rather than on the
 * kitchen, and saying "past the moment it had to be started" about it would be
 * nonsense — so the rule text follows the tab.
 */
function bandRuleKey(band: BoardBand, filter: BoardTab) {
  if (band === 'late') {
    return filter === 'ready'
      ? ('merchant.orders.bandLateReadyRule' as const)
      : ('merchant.orders.bandLateRule' as const);
  }
  if (band === 'now') {
    return filter === 'ready'
      ? ('merchant.orders.bandReadyNowRule' as const)
      : ('merchant.orders.bandNowRule' as const);
  }
  if (band === 'next' && filter === 'ready') return 'merchant.orders.bandReadyNextRule' as const;
  return 'merchant.orders.bandNextRule' as const;
}

/**
 * The green fill for the one action that confirms a handover.
 *
 * Filling it with the success tone rather than the brand colour marks it as a
 * different kind of action from the transitions that come before it. The hover
 * and pressed states are restated because the button's own primary-coloured
 * states would otherwise win on interaction.
 */
const SUCCESS_BUTTON = 'bg-success hover:bg-success/90 active:bg-success/90';

/**
 * The order queue with its status filter.
 *
 * @returns The queue screen.
 */
export function OrdersScreen() {
  const theme = useTheme();
  const t = useT();
  const toast = useToast();
  const { queue, loading, initial, error, reload, applyOrder, connection } = useOrders();
  // The rail is the only chrome beside the queue now that the cook's list has a
  // page of its own; the grid gets whatever else the screen has. The rail is
  // estimated at its expanded width, so collapsing it makes the cards wider
  // rather than changing how many there are — a board that reshuffles its column
  // count when the nav is toggled is worse than one that keeps its shape.
  const { width } = useResponsive();
  const CHROME_WIDTH = 300;
  // A floor, not a target: the count is the most columns that still leave every
  // card at least this wide. Cards are read from a counter a metre away, and a
  // dish with options wraps at anything narrower — so width is bought with
  // columns, deliberately. 380 is where two columns still fit a 1080pt iPad with
  // the rail expanded, which is the narrowest board this is built for; going
  // wider drops it to one column of full-width cards, which is worse than any
  // amount of wrapping.
  const CARD_MIN_WIDTH = 380;
  const columns = Math.max(1, Math.floor((width - CHROME_WIDTH) / CARD_MIN_WIDTH));
  const action = useAction();

  const [filter, setFilter] = useState<Filter>('all');
  /** The day's finished orders, which are not part of the live board. */
  const [showToday, setShowToday] = useState(false);
  /**
   * How the cards are ordered inside each band.
   *
   * The board's own answer is urgency — earliest deadline first — but a kitchen
   * that works the queue in the order it arrived wants to see the first order of
   * the rush at the top. The bands themselves never move; this only decides what
   * leads inside them.
   */
  const [ordering, setOrdering] = useState<BoardOrder>('urgency');
  /**
   * The chip the merchant has pressed, if any.
   *
   * Single-select and a toggle: pressing the chip that is already on clears it. A
   * filter is a question asked once, and the strip beside the tabs is where the
   * answer is written down — a second one would need a filter bar to explain
   * itself, and this board already composes tabs, search, bands and sort.
   */
  const [chip, setChip] = useState<OrderChip | null>(null);

  /**
   * The orderings the control offers.
   *
   * A menu rather than a cycling button: there are three states and only one of
   * them is what the board does by itself, so a merchant who wants urgency back
   * should be able to name it instead of pressing through the others to reach it.
   */
  const ORDERINGS: { value: BoardOrder; labelKey: MessageKey }[] = [
    { value: 'urgency', labelKey: 'merchant.orders.sortUrgency' },
    { value: 'oldest', labelKey: 'merchant.orders.sortOldest' },
    { value: 'newest', labelKey: 'merchant.orders.sortNewest' },
  ];
  /** The shelf, opened: booked orders more than an hour out. */
  const [showLater, setShowLater] = useState(false);
  const [search, setSearch] = useState('');
  const [openOrder, setOpenOrder] = useState<Order | null>(null);
  /**
   * The camera sheet, and what it is doing.
   *
   * A till takes the customer's code both ways: typed at the counter, or held up
   * to the camera. The camera lives in a sheet beside the field rather than on a
   * page of its own, because the order it finds is this page's detail — the
   * merchant scans and hands over without leaving the board.
   */
  const [scanOpen, setScanOpen] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<Order | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  /**
   * Why the merchant is cancelling. Nothing preselected: a default that is
   * usually wrong is worse than none, and the reason is what the customer's
   * history is built from — so it should be said rather than accepted.
   */
  const [cancelCode, setCancelCode] = useState<CancelChoice>('');
  /** Blocking rides with the cancellation but is a separate act. */
  const [blockOnCancel, setBlockOnCancel] = useState(false);
  const [busyOrderId, setBusyOrderId] = useState<string | null>(null);
  // Separate from the status write: a card can carry both buttons, and the spinner
  // has to sit on the one that was pressed rather than on whichever is checked first.
  const [busyPaidOrderId, setBusyPaidOrderId] = useState<string | null>(null);

  /** A cancel reason, or the empty string for no choice yet. */
  type CancelChoice = CancelCode | '';

  /**
   * The order a followed pickup link named, opened over the board on arrival.
   *
   * `/pickup/<token>` is what the customer's QR opens in the phone's camera, and
   * it resolves the token and lands here with it. Opening the sheet on the board
   * rather than on a page of its own is what makes a scan and a link the same
   * gesture: the order opens with the queue behind it, its actions are the board's
   * own, and nothing about following a URL hands the order over.
   *
   * The token is cleared from the address as soon as it is in hand, so the board
   * is left at a plain `/orders` — closing the sheet must not put the order back
   * on screen, and a refresh must not re-open it.
   */
  const [params, setParams] = useSearchParams();
  const openToken = params.get('open');

  useEffect(() => {
    if (!openToken) return;

    let alive = true;
    void (async () => {
      try {
        const found = await fetchOrderByPickupToken(supabase, openToken);
        if (alive && found) setOpenOrder(found);
      } catch {
        // A link that names nothing simply lands on the board: the token was
        // already resolved once by the route that sent us here, so a second
        // failure is a race, not an answer the merchant needs.
      } finally {
        if (alive) setParams({}, { replace: true });
      }
    })();

    return () => {
      alive = false;
    };
  }, [openToken, setParams]);

  // ---------------------------------------------------------------------------
  // Filtering and banding
  // ---------------------------------------------------------------------------

  /**
   * The status tab. This is the only filter: the bands below are an ordering,
   * so switching tabs removes members and never re-sorts or re-groups what is
   * left.
   */
  const inTab = useMemo(() => queue.filter((order) => inBoardTab(order, filter)), [queue, filter]);

  /**
   * The clock the bands are read against.
   *
   * `boardBand` asks what time it is, so grouping by it is a function of the
   * clock as much as of the queue — and a memo keyed only on the queue froze the
   * board the moment the queue went quiet. An order that crossed into Late sat in
   * Now until something else happened to change the data.
   *
   * Quantised rather than the raw tick: the grouping is rebuilt when this number
   * changes, so a one-second clock would regroup every card every second. One
   * `now` for every order, so no two cards are judged against different clocks.
   */
  const bandAt = Math.floor(useNow().getTime() / BAND_TICK_MS) * BAND_TICK_MS;

  const matched = useMemo(() => {
    const needle = search.trim().toLowerCase();
    // The chip is asked first because it is the coarsest question, and it is the
    // one the strip reports.
    const inChip =
      chip === null ? inTab : inTab.filter((order) => orderChips(order, bandAt).includes(chip));
    if (!needle) return inChip;
    return inChip.filter((order) => matchesSearch(order, needle));
  }, [inTab, search, chip, bandAt]);

  /**
   * The board, grouped by the moment each order has to be acted on.
   *
   * Sorted first, so every band keeps one order throughout. Urgency is the
   * default — paid first, then earliest deadline, then whoever has waited longest
   * — because the deadline and the arrival are not the same promise: a walk-up one
   * minute past its acknowledgement would otherwise lead a booking whose start-by
   * is six minutes gone. Payment leads both, since a paid ticket is one a customer
   * has committed to and an unpaid one has committed to nothing. A counter that
   * would rather work the queue in the order it arrived can say so with the sort
   * control, and those two orderings ignore the money.
   */
  const bands = useMemo(() => {
    const groups: Record<BoardBand, Order[]> = {
      late: [],
      now: [],
      next: [],
      later: [],
    };
    const compare = boardComparator(ordering);
    for (const order of [...matched].sort(compare)) groups[boardBand(order, bandAt)].push(order);
    return groups;
  }, [matched, bandAt, ordering]);

  /** Each tab's size and how much of it is late, for the bar. */
  const counts = useMemo(() => {
    // Over the chip's set, not the whole queue: with a filter on, a badge that
    // still said 96 would be describing orders the merchant cannot see.
    const shown = chip === null ? queue : queue.filter((o) => orderChips(o, bandAt).includes(chip));
    const tab = (value: BoardTab) => {
      const members = shown.filter((order) => inBoardTab(order, value));
      return {
        total: members.length,
        late: members.filter((order) => boardBand(order, bandAt) === 'late').length,
      };
    };
    return {
      all: tab('all'),
      accept: tab('accept'),
      cooking: tab('cooking'),
      ready: tab('ready'),
    };
  }, [queue, chip, bandAt]);

  /**
   * Today: the shop's own calendar day of closed orders, newest first.
   *
   * Not banded, and not drawn from the live queue: a finished order has no
   * deadline, and a live one does. The search is the server's here — the field
   * stays on screen in this mode, and a filter that is visible and does nothing
   * is worse than one that is not there.
   */
  const today = useDayOrders(showToday, search);

  /** Whether the counter has typed something. Blank and settled are the same question. */
  const hasSearch = search.trim() !== '';

  /** The far end of the shelf, for the line that stands in for it. */
  const later = bands.later;

  /**
   * The shelf's one line: how many are out there, and the first and last
   * deadlines among them. Deadlines rather than slots, because a walk-up with a
   * long quote sits in this band too and has no slot to name.
   */
  const shelfSummary = useMemo(() => {
    const first = later[0];
    if (!first) return { times: '', quiet: '' };
    // Clock times rather than day-stamped ones: the shelf only ever holds work
    // for later today, so "Today 08:45" spends most of the line saying the one
    // thing that is already known.
    const when = (order: Order) => {
      const due = actBy(order);
      return due === null ? '' : formatClock(new Date(due).toISOString(), order.timezone);
    };
    const last = later[later.length - 1];
    const next = actBy(first);
    return {
      times: t('merchant.orders.shelfNext', {
        next: when(first),
        last: last ? when(last) : '',
      }),
      // By construction the shelf holds nothing due inside the hour, so this is
      // always a real stretch of quiet rather than "nothing due for 40 seconds".
      quiet:
        next === null
          ? ''
          : t('merchant.orders.shelfNothing', {
              duration: formatDuration((next - bandAt) / 1000, t),
            }),
    };
  }, [later, bandAt, t]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  /**
   * Resolves a scanned code to its order and opens it.
   *
   * Read-only on purpose: a scan names an order, it does not hand one over. The
   * merchant sees what is actually in front of them — status, items, code — and
   * presses the action that fits, from the same sheet and with the same press as
   * an order opened from a card. Completing the moment a code is read spends the
   * code before anyone has looked at the bag.
   */
  const handleScan = useCallback(
    async (scanned: string) => {
      if (scanBusy) return;
      const target = scanTarget(scanned);
      if (!target) {
        setScanError(t('merchant.orders.scanForeign'));
        return;
      }

      setScanBusy(true);
      setScanError(null);
      try {
        const found =
          target.kind === 'pickup'
            ? await fetchOrderByPickupToken(supabase, target.token)
            : await fetchOrderByCode(supabase, target.code);

        if (!found) {
          setScanError(t('merchant.orders.scanNoMatch'));
          return;
        }
        setScanOpen(false);
        setOpenOrder(found);
      } catch (cause) {
        setScanError(cause instanceof Error ? cause.message : String(cause));
      } finally {
        setScanBusy(false);
      }
    },
    [scanBusy, t],
  );

  const advance = useCallback(
    async (order: Order, status: OrderStatus) => {
      setBusyOrderId(order.id);
      const updated = await action.run(() => setOrderStatus(supabase, order.id, status));

      if (updated) {
        applyOrder(updated);
        // One bar carries a run of the same status. A till handing over orders one
        // after another otherwise stacks a bar per order over the cards it is
        // working — and each bar repeats what the card behind it already says.
        const status = orderStatusLabel(updated.status, t);
        toast.show(
          t('merchant.orders.statusToast', {
            number: `#${updated.order_number}`,
            status,
          }),
          {
            tone: updated.status === 'ready' ? 'success' : 'neutral',
            key: `status:${updated.status}`,
            run: (count) => t('merchant.orders.statusRunToast', { count, status }),
          },
        );
        setOpenOrder((current) => (current?.id === updated.id ? updated : current));
      } else {
        // The server rejected the transition; the local board may be stale.
        void reload();
      }

      setBusyOrderId(null);
    },
    [action, applyOrder, reload, toast, t],
  );

  /**
   * Records or clears the money, in one press either way.
   *
   * Not a status change and not routed through `advance`: the order keeps whatever
   * the kitchen is doing with it, and this only says whether the counter has been
   * paid. It is reversible because a mis-tap has to be — which is the reason the
   * fact is a column rather than a stage.
   */
  const togglePaid = useCallback(
    async (order: Order) => {
      const paid = order.paid_at === null;
      setBusyPaidOrderId(order.id);
      const updated = await action.run(() => setOrderPaid(supabase, order.id, paid));

      if (updated) {
        applyOrder(updated);
        setOpenOrder((current) => (current?.id === updated.id ? updated : current));
        toast.show(
          t(updated.paid_at ? 'merchant.orders.paidToast' : 'merchant.orders.unpaidToast', {
            number: `#${updated.order_number}`,
          }),
          { tone: updated.paid_at ? 'success' : 'neutral' },
        );
      } else {
        // The server refused; the local board may be stale.
        void reload();
      }

      setBusyPaidOrderId(null);
    },
    [action, applyOrder, reload, toast, t],
  );

  const confirmCancel = useCallback(async () => {
    if (!cancelling || !cancelCode) return;
    const order = cancelling;
    const updated = await action.run(() =>
      setOrderStatus(supabase, order.id, 'cancelled', cancelReason || undefined, cancelCode),
    );

    if (updated) {
      applyOrder(updated);
      // The cancellation is said first because the surface keeps one plain bar:
      // the cancelled order has already left the board, while the block is nowhere
      // else on screen — so the block is the one shown last.
      toast.show(
        t('merchant.orders.cancelledToast', {
          number: `#${updated.order_number}`,
        }),
        { tone: 'danger' },
      );
      // Blocking is a separate act that happens to ride along. A failure to
      // block must not make the cancellation look like it failed, so it is
      // reported on its own and the cancel stands either way.
      if (blockOnCancel && order.customer_phone) {
        try {
          await blockNumber(supabase, order.restaurant_id, order.customer_phone, cancelCode);
          toast.show(t('merchant.orders.blockedToast'), { tone: 'danger' });
        } catch {
          toast.show(t('merchant.orders.blockFailedToast'), { tone: 'danger' });
        }
      }
      setCancelling(null);
      setCancelReason('');
      setCancelCode('');
      setBlockOnCancel(false);
      setOpenOrder(null);
    }
  }, [cancelling, cancelCode, cancelReason, blockOnCancel, action, applyOrder, toast, t]);

  // ---------------------------------------------------------------------------

  if (initial) {
    return (
      <Screen>
        <LoadingState message={t('merchant.orders.loading')} />
      </Screen>
    );
  }

  const refreshLabel = loading ? t('common.refreshing') : t('common.refresh');
  const cancelLabel = t('merchant.orders.cancelOrder');
  const handedOverLabel = t('merchant.orders.handedOverFull');
  const busyOnOpenOrder = openOrder !== null && busyOrderId === openOrder.id;
  const payingOnOpenOrder = openOrder !== null && busyPaidOrderId === openOrder.id;

  /**
   * The late count that rides on a tab, or nothing when none of it is late.
   *
   * A tab hides the work in the other tabs, so the one number that has to travel
   * with it is how much of its own work is behind.
   */
  const lateHint = (count: number) =>
    count > 0 ? t('merchant.orders.lateCount', { count }) : undefined;

  /** A band's heading. */
  const bandHeading = (band: BoardBand, list: Order[]) => (
    <Row gap="sm" align="baseline" className="pt-1">
      <Text
        className={cn(
          'text-xs font-bold tracking-wider uppercase',
          band === 'late'
            ? 'text-destructive'
            : band === 'now'
              ? 'text-warning'
              : 'text-muted-foreground',
        )}>
        {t(BAND_LABEL[band])}
      </Text>
      <Text className="text-muted-foreground text-xs font-bold">{list.length}</Text>
      <Text className="text-muted-foreground text-xs">{t(bandRuleKey(band, filter))}</Text>
    </Row>
  );

  const renderCards = (list: Order[]) => (
    <CardGrid columns={columns}>
      {list.map((order) => (
        <OrderCard
          key={order.id}
          order={order}
          busy={busyOrderId === order.id}
          paying={busyPaidOrderId === order.id}
          onTogglePaid={(target) => void togglePaid(target)}
          onAdvance={(status) => void advance(order, status)}
          onOpen={() => setOpenOrder(order)}
          activeChip={chip}
          onChipPress={(next) => setChip((current) => (current === next ? null : next))}
          onStatusPress={() => {
            // A chip narrows the board; the status asks for another one of them.
            setChip(null);
            setFilter(TAB_FOR_STATUS[order.status]);
          }}
        />
      ))}
    </CardGrid>
  );

  return (
    <Screen constrain={false}>
      <Stack gap="lg" className="flex-1">
        <PageHeading
          title={showToday ? t('merchant.orders.todayTitle') : t('merchant.orders.title')}
          // 200: this screen's action is a search field that flexes beside three
          // buttons, so the title block gives up its floor sooner than most.
          minWidth={200}
          subtitle={
            <Row gap="sm" align="center">
              <HeadingStat>
                {showToday
                  ? t('merchant.orders.todaySubtitle', { count: today.total })
                  : chip === null
                    ? t('merchant.orders.liveCount', { count: queue.length })
                    : t('merchant.orders.showingOfLive', {
                        shown: matched.length,
                        total: queue.length,
                      })}
                {/* The rest are properties of that number, not more numbers to add
                    to it — the late count and the ready count overlap, and a row of
                    bare counts reads as a sum that will not come out. Their tones
                    stay: red is behind, green is food on the pass. */}
                {!showToday && (counts.all.late > 0 || counts.ready.total > 0) ? ' (' : ''}
                {!showToday && counts.all.late > 0 ? (
                  <HeadingStat tone="destructive">
                    {t('merchant.orders.lateCount', { count: counts.all.late })}
                  </HeadingStat>
                ) : null}
                {!showToday && counts.all.late > 0 && counts.ready.total > 0 ? ' · ' : ''}
                {!showToday && counts.ready.total > 0 ? (
                  <HeadingStat tone="success">
                    {t('merchant.orders.readyCount', { count: counts.ready.total })}
                  </HeadingStat>
                ) : null}
                {!showToday && (counts.all.late > 0 || counts.ready.total > 0) ? ')' : ''}
              </HeadingStat>
              {/* Only the counts with something to say: how much is behind, and
                  how much is waiting at the counter. An absent count is the
                  signal, so a calm board reads as one line rather than three —
                  and a board with ready work says so, because "there is food on
                  the pass" is the one thing the counter must not have to open a
                  tab to find out. */}

              {connection === 'offline' ? (
                <ToneBadge label={t('merchant.connection.offline')} tone="warning" size="sm" />
              ) : null}
            </Row>
          }
          action={
            <Row gap="sm" align="center" wrap justify="end" className="min-w-0 flex-1 basis-[22rem]">
              {/* The controls travel as one group, and the search field is the
                  part that gives. Laid out flat, a narrow header wraps one item
                  at a time — which is what stranded Refresh on a line of its own
                  under nothing, and left the search field nowhere to shrink into.
                  Two rules do the work: the field flexes between a floor and a
                  ceiling, and the three buttons are a single item, so they move
                  together or not at all. */}
              <View className="min-w-[150px] max-w-[260px] flex-1">
                <TextField
                  value={search}
                  onChangeText={setSearch}
                  placeholder={t('merchant.orders.searchPlaceholder')}
                  autoCapitalize="none"
                  // The till's camera, inside the field the counter's hands are
                  // already on: a customer holding up a code is the same question as
                  // one reading it out, so the two belong to one control rather than
                  // to a page of their own. A scan opens the order it names; it does
                  // not collect it.
                  suffix={
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onPress={() => {
                            setScanError(null);
                            setScanOpen(true);
                          }}
                          accessibilityLabel={t('merchant.orders.scanCode')}>
                          <Icon as={ScanLine} aria-hidden className="text-muted-foreground size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <Text>{t('merchant.orders.scanCode')}</Text>
                      </TooltipContent>
                    </Tooltip>
                  }
                />
              </View>

              <Row gap="sm" align="center" className="shrink-0">
                {/* The order the counter wants to work in. The trigger shows the one
                in force; the menu lists all three, so urgency is reachable in one
                press from anywhere. Compact it keeps the menu — and so the tick on
                the ordering in force — and gives up only the word on the button. */}
                {showToday ? null : (
                  <DropdownMenu>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                          {/* Fixed width, and the label laid out inside it rather
                          than sized to its own text: the orderings are three
                          different lengths, and a trigger sized to its own text
                          re-flows the row on every change. */}
                          <Button
                            variant="secondary"
                            className="w-9 px-0"
                            accessibilityLabel={t('merchant.orders.sortLabel')}>
                            <Icon as={ArrowUpDown} aria-hidden className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                      </TooltipTrigger>
                      <TooltipContent>
                        <Text>{t('merchant.orders.sortLabel')}</Text>
                      </TooltipContent>
                    </Tooltip>
                    <DropdownMenuContent align="end" className="min-w-[10rem]">
                      {/* A radio group, not checkboxes: exactly one of these is in
                      force, and the menu is where the other two are named. */}
                      <DropdownMenuRadioGroup
                        value={ordering}
                        onValueChange={(v) => setOrdering(v as BoardOrder)}>
                        {ORDERINGS.map((option) => (
                          <DropdownMenuRadioItem key={option.value} value={option.value}>
                            <Text>{t(option.labelKey)}</Text>
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* History is not the live queue, so it is a mode with a way back
                rather than another tab in the bar. */}
                <IconControl
                  label={
                    showToday ? t('merchant.orders.backToBoard') : t('merchant.orders.todayTitle')
                  }
                  icon={showToday ? ArrowLeft : CalendarDays}
                  onPress={() => setShowToday((on) => !on)}
                />

                <IconControl
                  label={refreshLabel}
                  icon={RefreshCw}
                  onPress={() => void reload()}
                  disabled={loading}
                />
              </Row>
            </Row>
          }
        />

        <Stack gap="lg" className="min-w-0 flex-1">
          {showToday ? null : (
            <Row gap="sm" align="center" wrap>
              <SegmentedControl<Filter>
              options={[
                {
                  value: 'all',
                  label: t('merchant.orders.tabAll'),
                  badge: counts.all.total,
                  hint: lateHint(counts.all.late),
                },
                {
                  value: 'accept',
                  label: t('merchant.orders.tabAccept'),
                  badge: counts.accept.total,
                  hint: lateHint(counts.accept.late),
                },
                {
                  value: 'cooking',
                  label: t('merchant.orders.tabCooking'),
                  badge: counts.cooking.total,
                  hint: lateHint(counts.cooking.late),
                },
                {
                  value: 'ready',
                  label: t('merchant.orders.tabReady'),
                  badge: counts.ready.total,
                  hint: lateHint(counts.ready.late),
                },
              ]}
                value={filter}
                onChange={setFilter}
              />
              {/* What is on, and the way off it. The chip itself is marked on every
                  card too, so the narrowing is legible where the merchant is
                  looking rather than only here. */}
              {chip === null ? null : (
                <Pressable
                  onPress={() => setChip(null)}
                  accessibilityRole="button"
                  accessibilityLabel={t('merchant.orders.clearFilter')}>
                  <View className="border-primary bg-primary-soft flex-row items-center gap-2 rounded-full border px-3 py-1">
                    <Text className="text-primary-strong text-xs font-semibold">
                      {t('merchant.orders.showingChip', { chip: t(CHIP_LABEL_KEYS[chip]) })}
                    </Text>
                    <Text className="text-primary-strong text-xs font-bold">✕</Text>
                  </View>
                </Pressable>
              )}
            </Row>
          )}

          {action.error ? (
            <ErrorBanner message={action.error} onRetry={() => action.clearError()} />
          ) : null}

          {error ? <ErrorBanner message={error} onRetry={() => void reload()} /> : null}

          {showToday ? (
            <Stack gap="lg">
              {/* A failed page is stated rather than shown as an empty day: the
                  two look identical otherwise, and one of them is a lie. */}
              {today.error ? (
                <ErrorBanner message={today.error} onRetry={today.reload} />
              ) : null}

              {today.loading && today.orders.length === 0 ? (
                <LoadingState message={t('merchant.orders.loading')} />
              ) : today.orders.length === 0 ? (
                today.error ? null : (
                  <EmptyState
                    icon={CheckCheck}
                    title={t(
                      hasSearch ? 'merchant.orders.todayNoMatch' : 'merchant.orders.todayEmpty',
                    )}
                    message={t(
                      hasSearch
                        ? 'merchant.orders.todayNoMatchHint'
                        : 'merchant.orders.todayEmptyHint',
                    )}
                  />
                )
              ) : (
                <>
                  {renderCards(today.orders)}
                  {/* The day is a record and can be longer than one page. The
                      count is the day's own, so "50 of 212" says both how far the
                      list has been read and that it is not the whole of it. */}
                  {today.more ? (
                    <Stack gap="sm" className="items-center">
                      <Text className="text-muted-foreground text-xs">
                        {t('merchant.orders.todayShown', {
                          shown: today.orders.length,
                          total: today.total,
                        })}
                      </Text>
                      <Button
                        variant="secondary"
                        onPress={today.loadMore}
                        disabled={today.loadingMore}
                        accessibilityLabel={t('merchant.orders.todayLoadMore')}
                        aria-busy={today.loadingMore}>
                        {today.loadingMore ? (
                          <ActivityIndicator color={theme.palette.foreground} size="small" />
                        ) : (
                          <Text>{t('merchant.orders.todayLoadMore')}</Text>
                        )}
                      </Button>
                    </Stack>
                  ) : null}
                </>
              )}
            </Stack>
          ) : matched.length === 0 ? (
            <EmptyState
              icon={CheckCheck}
              title={t('merchant.orders.emptyActive')}
              message={t('merchant.orders.emptyActiveHint')}
            />
          ) : (
            <Stack gap="lg">
              {BANDS.map((band) =>
                bands[band].length === 0 ? null : (
                  <Stack key={band} gap="sm">
                    {bandHeading(band, bands[band])}
                    {renderCards(bands[band])}
                  </Stack>
                ),
              )}

              {/* The day's far end, closed by default: booked orders more than
                  an hour out are the afternoon's plan, not the queue. */}
              {later.length > 0 ? (
                <Pressable
                  onPress={() => setShowLater((on) => !on)}
                  accessibilityRole="button"
                  aria-expanded={showLater}
                  accessibilityLabel={t('merchant.orders.shelfBooked', {
                    count: later.length,
                  })}
                  className="border-border bg-muted/40 active:opacity-70 flex-row items-center gap-2.5 rounded-lg border px-3 py-2.5">
                  {/* The chevron leads rather than trails: this is a disclosure
                      first and a sentence second, and a marker on the far right
                      is read after the line it belongs to. */}
                  <Icon
                    as={showLater ? ChevronUp : ChevronDown}
                    size={16}
                    aria-hidden
                    className="text-muted-foreground"
                  />
                  <Text numberOfLines={1} className="text-muted-foreground shrink text-sm">
                    <Text className="text-foreground text-sm font-bold">
                      {t('merchant.orders.shelfBooked', {
                        count: later.length,
                      })}
                    </Text>
                    {shelfSummary.times ? ` — ${shelfSummary.times}` : ''}
                  </Text>
                  <Spacer />
                  {shelfSummary.quiet ? (
                    <ToneBadge label={shelfSummary.quiet} tone="neutral" size="sm" />
                  ) : null}
                </Pressable>
              ) : null}

              {showLater && later.length > 0 ? renderCards(later) : null}
            </Stack>
          )}
        </Stack>
      </Stack>

      {/* ------------------------------------------------------------------ */}
      {/* Detail                                                             */}
      {/* ------------------------------------------------------------------ */}

      <Modal
        visible={openOrder !== null}
        onClose={() => setOpenOrder(null)}
        title={
          openOrder
            ? t('merchant.orders.detailTitle', {
                number: `#${openOrder.order_number}`,
              })
            : ''
        }
        subtitle={
          openOrder ? `${openOrder.customer_name} · ${openOrder.customer_phone}` : undefined
        }
        // No `maxWidth`, so it takes the kit's default: the detail and the cancel
        // form are the same width, and neither carries a number of its own.
        //
        // `exactWidth`, because a centred sheet is otherwise as wide as its widest
        // row: an order with a callout or a long dish name opened a wide modal and
        // one without opened a narrow modal, which is where the action labels
        // started wrapping. A panel read in columns wants its width stated.
        exactWidth
        footer={
          openOrder ? (
            <Stack gap="sm">
              {/* Every action of every status, in the footer, because the sheet's
                  own contract is that "the header and footer stay put, which keeps
                  the primary action reachable however long the content is". Only the
                  ready state's hand-over was here, so Accept and Mark ready scrolled
                  away behind a long item list — the two actions a counter presses
                  most. */}
              {openOrder.status === 'pending' ||
              openOrder.status === 'accepted' ||
              openOrder.status === 'preparing' ? (
                <Transitions
                  order={openOrder}
                  busy={busyOnOpenOrder}
                  onChange={(status) => void advance(openOrder, status)}
                />
              ) : null}
              {openOrder.status === 'ready' ? (
                <Button
                  variant="default"
                  size="lg"
                  className={cn('w-full', SUCCESS_BUTTON)}
                  disabled={busyOnOpenOrder}
                  onPress={() => void advance(openOrder, 'completed')}
                  accessibilityLabel={handedOverLabel}
                  aria-busy={busyOnOpenOrder}>
                  {busyOnOpenOrder ? (
                    <ActivityIndicator color={theme.palette.primaryForeground} size="small" />
                  ) : (
                    <Text>{handedOverLabel}</Text>
                  )}
                </Button>
              ) : null}
              <Row gap="sm">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onPress={() => {
                    window.location.href = `tel:${openOrder.customer_phone}`;
                  }}
                  accessibilityLabel={t('merchant.orders.callCustomer')}>
                  <Text>{t('merchant.orders.callCustomer')}</Text>
                </Button>
                {openOrder.status !== 'completed' && openOrder.status !== 'cancelled' ? (
                  <Button
                    variant="secondary"
                    className="flex-1"
                    onPress={() => {
                      setCancelling(openOrder);
                      setOpenOrder(null);
                    }}
                    accessibilityLabel={cancelLabel}>
                    <Text>{cancelLabel}</Text>
                  </Button>
                ) : null}
              </Row>
            </Stack>
          ) : null
        }>
        {openOrder ? (
          <Stack gap="lg">
            <OrderDetail order={openOrder} />
            {/* The money, in the sheet's body rather than its footer. The footer is
                the order's own flow — accept, mark ready, hand over — and payment is
                not a step in it: it can happen before any of them or after all of
                them, so it reads as a fact with a control rather than as the next
                thing to do. */}
            <Row spread gap="md" align="center" className="border-border border-t pt-4">
              <Stack gap="xs">
                <Text className="text-base font-bold">{t('merchant.orders.payment')}</Text>
                <Text className="text-muted-foreground text-sm">
                  {openOrder.paid_at
                    ? t('merchant.orders.paidAt', {
                        time: formatClock(openOrder.paid_at, openOrder.timezone),
                      })
                    : t('merchant.orders.notPaid')}
                </Text>
              </Stack>
              <Button
                variant={openOrder.paid_at ? 'secondary' : 'default'}
                size="sm"
                disabled={busyOnOpenOrder || payingOnOpenOrder}
                aria-busy={payingOnOpenOrder}
                onPress={() => void togglePaid(openOrder)}
                accessibilityLabel={
                  openOrder.paid_at
                    ? t('merchant.orders.markUnpaid')
                    : t('merchant.orders.markPaid')
                }>
                <Text>
                  {openOrder.paid_at
                    ? t('merchant.orders.markUnpaid')
                    : t('merchant.orders.markPaid')}
                </Text>
              </Button>
            </Row>
          </Stack>
        ) : null}
      </Modal>

      {/* ------------------------------------------------------------------ */}
      {/* Scan                                                               */}
      {/* ------------------------------------------------------------------ */}

      <Modal
        visible={scanOpen}
        onClose={() => {
          setScanOpen(false);
          setScanError(null);
        }}
        title={t('merchant.orders.scanTitle')}
        subtitle={t('merchant.orders.scanHint')}
        // Wider than the default sheet: the viewfinder is 4:3 and the guide is
        // sized from its shorter side, so a narrow sheet would shrink the camera
        // and the code with it.
        maxWidth={640}>
        <Stack gap="md">
          {/* `active` follows the sheet, so closing it releases the camera rather
              than leaving the indicator lit behind a dismissed dialog. */}
          <QrScanner
            active={scanOpen}
            paused={scanBusy}
            onScan={(text) => void handleScan(text)}
          />
          {scanBusy ? <LoadingState message={t('merchant.orders.scanLooking')} /> : null}
          {/* The camera's own failures are reported inside the viewfinder; this is
              the lookup failing, which the camera knows nothing about. */}
          {scanError ? <ErrorBanner message={scanError} /> : null}
        </Stack>
      </Modal>

      {/* ------------------------------------------------------------------ */}
      {/* Cancellation                                                       */}
      {/* ------------------------------------------------------------------ */}

      <Modal
        visible={cancelling !== null}
        onClose={() => {
          setCancelling(null);
          setCancelReason('');
        }}
        title={
          cancelling
            ? t('merchant.orders.cancelTitle', {
                number: `#${cancelling.order_number}`,
              })
            : ''
        }
        subtitle={t('merchant.orders.cancelSubtitle')}
        // The same width as the detail it is opened from, and stated for the same
        // reason: a centred sheet is otherwise as wide as its widest row.
        exactWidth
        footer={
          <Row gap="sm" justify="end">
            <Button
              variant="secondary"
              onPress={() => {
                setCancelling(null);
                setCancelReason('');
              }}
              disabled={action.pending}
              accessibilityLabel={t('merchant.orders.cancelKeep')}>
              <Text>{t('merchant.orders.cancelKeep')}</Text>
            </Button>
            <Button
              variant="destructive"
              onPress={() => void confirmCancel()}
              disabled={action.pending || !cancelCode}
              accessibilityLabel={t('merchant.orders.cancelConfirm')}
              aria-busy={action.pending}>
              {action.pending ? (
                <ActivityIndicator color={theme.palette.destructiveForeground} size="small" />
              ) : (
                <Text>{t('merchant.orders.cancelConfirm')}</Text>
              )}
            </Button>
          </Row>
        }>
        <Stack gap="md">
          <Callout
            tone="warning"
            title={t('merchant.orders.cancelWarning')}
            message={t('merchant.orders.cancelWarningHint')}
          />
          {/* Why, before what: the reason is what the customer's history is
              built from, so it is asked first and nothing is chosen for them. */}
          <Stack gap="xs">
            <Text className="text-sm font-semibold">{t('merchant.orders.cancelWhy')}</Text>
            {/* An empty value stands for no choice: nothing is selected until
                the merchant taps, and the control renders an unmatched value
                as no selection. */}
            <SegmentedControl<CancelChoice>
              options={[
                { value: 'no_show', label: t('merchant.orders.reasonNoShow') },
                {
                  value: 'sold_out',
                  label: t('merchant.orders.reasonSoldOut'),
                },
                { value: 'other', label: t('merchant.orders.reasonOther') },
              ]}
              value={cancelCode}
              onChange={setCancelCode}
            />
            <Text className="text-subtle-foreground text-xs">
              {t('merchant.orders.cancelWhyHint')}
            </Text>
          </Stack>
          <TextField
            label={t('merchant.orders.cancelReason')}
            value={cancelReason}
            onChangeText={setCancelReason}
            placeholder={t('merchant.orders.cancelReasonPlaceholder')}
            multiline
            numberOfLines={2}
            hint={t('merchant.orders.cancelReasonHint')}
          />
          {/* A separate act that happens to ride along: blocking is about the
              number, not this order, so it is a switch rather than a fourth
              reason. */}
          <Pressable
            onPress={() => setBlockOnCancel((on) => !on)}
            accessibilityRole="checkbox"
            aria-checked={blockOnCancel}
            className="flex-row items-center gap-2">
            <View
              className={cn(
                'h-5 w-5 items-center justify-center rounded border',
                blockOnCancel ? 'border-destructive bg-destructive' : 'border-input',
              )}>
              {blockOnCancel ? <Text className="text-background text-xs font-bold">✓</Text> : null}
            </View>
            <Text className="text-base">{t('merchant.orders.blockNumber')}</Text>
          </Pressable>
          {blockOnCancel ? (
            <Text className="text-subtle-foreground text-xs">
              {t('merchant.orders.blockedNote')}
            </Text>
          ) : null}
        </Stack>
      </Modal>
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Detail body
// ---------------------------------------------------------------------------

/**
 * The forward transitions a merchant can make from the detail view.
 *
 * In the sheet's footer rather than its body: the body scrolls, and these are the
 * buttons a counter reaches for most — a long item list used to push them off the
 * bottom of the screen, which is the opposite of what the footer is for.
 *
 * @param props The order, the busy flag, and the status handler.
 * @returns The action the order's status offers next.
 */
function Transitions({
  order,
  busy,
  onChange,
}: {
  order: Order;
  busy: boolean;
  onChange: (status: OrderStatus) => void;
}) {
  const t = useT();
  if (order.status === 'accepted') {
    return (
      <Row gap="sm">
        <Button
          variant="default"
          className="flex-1"
          onPress={() => onChange('preparing')}
          disabled={busy}
          accessibilityLabel={t('merchant.orders.startPreparing')}>
          <Text>{t('merchant.orders.startPreparing')}</Text>
        </Button>
        <Button
          variant="default"
          className={cn('flex-1', SUCCESS_BUTTON)}
          onPress={() => onChange('ready')}
          disabled={busy}
          accessibilityLabel={t('merchant.orders.markReady')}>
          <Text>{t('merchant.orders.markReady')}</Text>
        </Button>
      </Row>
    );
  }
  const label =
    order.status === 'pending' ? t('merchant.orders.accept') : t('merchant.orders.markReady');
  const next: OrderStatus = order.status === 'pending' ? 'accepted' : 'ready';
  return (
    <Button
      variant="default"
      className={cn('w-full', order.status !== 'pending' && SUCCESS_BUTTON)}
      onPress={() => onChange(next)}
      disabled={busy}
      accessibilityLabel={label}>
      <Text>{label}</Text>
    </Button>
  );
}

function OrderDetail({ order }: { order: Order }) {
  const t = useT();
  /**
   * Whether the kitchen's part is over.
   *
   * A made order keeps its pickup time — that is when the customer arrives — but
   * the start-by and the born-late note are advice about starting, and on a ready
   * order they read as a board that has not noticed the food is out.
   */
  const settled =
    order.status === 'ready' || order.status === 'completed' || order.status === 'cancelled';

  const steps = [
    { labelKey: 'merchant.orders.stepPlaced' as const, at: order.placed_at },
    {
      labelKey: 'merchant.orders.stepAccepted' as const,
      at: order.accepted_at,
    },
    {
      labelKey: 'merchant.orders.stepPreparing' as const,
      at: order.preparing_at,
    },
    { labelKey: 'merchant.orders.stepReady' as const, at: order.ready_at },
    {
      labelKey: 'merchant.orders.stepCollected' as const,
      at: order.completed_at,
    },
  ].filter((step) => step.at !== null);

  return (
    <Stack gap="lg">
      <Row spread gap="md" align="center">
        <StatusChip status={order.status} />
        {/* The number is the title, and the code has its own place further down:
            both were tried here and one of them was always repeating the other. */}
        <Text className="text-subtle-foreground text-xs">
          {t('common.itemCount', { count: totalItemCount(order) })}
        </Text>
      </Row>

      <Card className="p-3">
        <Stack gap="sm">
          {order.items.map((line) => (
            <Row key={line.id} spread gap="md">
              <Stack gap="xs" className="flex-1">
                <Text className="text-base">
                  <Text className="text-base font-bold">{line.quantity}× </Text>
                  {line.name}
                  {line.variant_name_snapshot ? ` · ${line.variant_name_snapshot}` : ''}
                  {(line.options ?? []).length > 0
                    ? ` · ${(line.options ?? []).map((option) => option.name).join(', ')}`
                    : ''}
                </Text>
                {line.discount_cents > 0 ? (
                  <Text className="text-success text-[11px]">
                    {line.discount_label ?? t('common.discount')} −
                    {formatMoney(line.discount_cents, order.currency)}
                  </Text>
                ) : null}
              </Stack>
              <Text className="text-base font-semibold">
                {formatMoney(line.line_total_cents, order.currency)}
              </Text>
            </Row>
          ))}
        </Stack>
      </Card>

      <Stack gap="sm">
        {/* Only when it is not the whole story. Without a discount or tax the
            subtotal and the total are the same number, and a receipt that says
            it twice reads as though something is missing from the middle. */}
        {order.discount_cents > 0 || order.tax_cents > 0 ? (
          <StatRow
            label={t('common.subtotal')}
            value={formatMoney(order.subtotal_cents, order.currency)}
          />
        ) : null}
        {order.discount_cents > 0 ? (
          <StatRow
            label={t('common.discount')}
            value={`−${formatMoney(order.discount_cents, order.currency)}`}
            tone="success"
          />
        ) : null}
        {order.tax_cents > 0 ? (
          <StatRow label={t('common.tax')} value={formatMoney(order.tax_cents, order.currency)} />
        ) : null}
        <StatRow
          label={t('common.total')}
          value={formatMoney(order.total_cents, order.currency)}
          emphasis
        />
      </Stack>

      {order.note ? <Callout tone="warning" title={t('common.note')} message={order.note} /> : null}

      <Stack gap="sm">
        <Text className="text-muted-foreground text-sm font-semibold">
          {t('merchant.orders.timeline')}
        </Text>
        {steps.map((step) => (
          <Row key={step.labelKey} spread gap="md">
            <Text className="text-muted-foreground text-xs">{t(step.labelKey)}</Text>
            <Text className="text-subtle-foreground text-xs">
              {formatDayTime(step.at!, order.timezone, undefined, t)}
            </Text>
          </Row>
        ))}
      </Stack>

      {/* Where a docket puts its code: after the history, before the promise, and
          centred so it reads as a stamp rather than as another field. The number in
          the title is the counter's — it is what gets called across — and this is
          the customer's, read off their own screen and the only identifier that is
          unique across restaurants. The search matches it, so this is what a
          merchant checks a customer's phone against. */}
      <View className="border-border border-t pt-3.5">
        <Text className="text-muted-foreground text-center text-[10px] font-bold tracking-[0.12em] uppercase">
          {t('merchant.orders.codeLabel')}
        </Text>
        <Text className="mt-1 text-center text-2xl font-bold tracking-[0.2em]">
          {order.code}
        </Text>
      </View>

      <Stack gap="sm">
        <Text className="text-muted-foreground text-sm font-semibold">
          {t('merchant.orders.estimate')}
        </Text>
        <Text className="text-subtle-foreground text-xs">
          {order.is_scheduled
            ? t('merchant.orders.estimateScheduled', {
                when: formatDayTime(order.eta_ready_at, order.timezone, undefined, t),
              })
            : t('merchant.orders.estimateQuoted', {
                minutes: Math.round(order.eta_seconds / 60),
                model: order.eta_model,
                samples:
                  order.eta_sample_count > 0
                    ? t('merchant.orders.estimateSamples', {
                        count: order.eta_sample_count,
                      })
                    : '',
                queue:
                  order.eta_queue_depth > 0
                    ? t('merchant.orders.estimateQueue', {
                        count: order.eta_queue_depth,
                      })
                    : '',
              })}
        </Text>
        {/* When the kitchen has to start, which is the deadline the board's
            urgency is judged against. A time rather than a countdown: it is a
            fact about the slot, not a running clock. */}
        {/* While there is still a kitchen to start something in. A made order
            keeps its pickup time — that is when the customer arrives — but a
            start-by and a "the kitchen never had a clear run at it" note are
            advice about starting, and there is nothing left to start. */}
        {order.is_scheduled && order.prep_start_at && !settled ? (
          <Text className="text-subtle-foreground text-xs">
            {t('merchant.orders.startBy', {
              when: formatDayTime(order.prep_start_at, order.timezone, undefined, t),
            })}
          </Text>
        ) : null}
        {/* Why the card is already red and in "Needs attention": the customer
            took the checkout's "very soon" warning, so this was behind the
            kitchen before it landed. Context for reading the order, not a
            control — there is nothing to do about it but cook. */}
        {bookedInsidePrep(order) && !settled ? (
          <Callout
            tone="warning"
            title={t('merchant.orders.bookedInsidePrep')}
            message={t('merchant.orders.bookedInsidePrepHint')}
          />
        ) : null}
        {order.customer_email ? (
          <Text className="text-subtle-foreground text-xs">
            {t('merchant.orders.receiptSent', { email: order.customer_email })}
          </Text>
        ) : (
          <Text className="text-subtle-foreground text-xs">{t('merchant.orders.receiptNone')}</Text>
        )}
      </Stack>

    </Stack>
  );
}
